import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  DisplayDateBody,
  DisplayDateHeader,
  DisplayDateWrapper,
  ErrorMessage,
  ErrorText,
  GrayText,
  GuideText,
  ImageUploadFormWrapper,
  ImageUploadLabel,
  ImageUploadWrapper,
  OptionWrapper,
  PopupBody,
  PopupContent,
  PopupFooter,
  PopupHeader,
  PopupHeaderTitle,
  PreviewBody,
  PreviewContentSummary,
  PreviewHeader,
  PreviewImg,
  PreviewTitle,
  OptionBody,
  PreviewWrapper,
  TitleOptionWrapper,
  VisuallyHiddenInput,
} from './styled';
import Label from '@/components/@IntranetLibrary/Label';
import { Box, Button, IconButton, Link, Modal } from '@mui/material';
import RHFDateTimePicker from '@/components/HookForm/RHFDateTimePicker';
import {
  base64ToFile,
  getMimeTypeByExtension,
  uploadImage,
  validateFile,
} from '@/components/file-thumbnail/utils';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import ImageCropEditor from '@/components/ImageCropEditor';
import useImageCropEditorProps, {
  AspectRange,
} from '@/components/ImageCropEditor/useImageCropEditorProps';
import { uuid4 } from '@sentry/utils';
import RHFRadio from '@/components/HookForm/RHFRadio';
import {
  BooleanOption,
  canHideTitleInList,
  isTitleShownInList,
} from '@/hooks/board/useBoardForm';
import Radio from '@/components/@IntranetLibrary/Radio';
import { ButtonWrapper } from '@/components/@IntranetLibrary/Button/ButtonWrapper';
import { stripHTML } from '@/utils';
import { BoardFormOptions } from '@/hooks/board/useBoardFormOptions';
import { PAGE_URL } from '@/constant/url';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

/**
 * 새소식·미노출 썸네일에 허용하는 가로세로비 범위.
 * 상한 2:1 은 메인 배너와 같은 값으로, 이보다 납작하면 목록에서 띠처럼 보인다.
 * 하한 3:4 는 모바일 1열에서 카드 하나가 화면 절반을 조금 넘는 선이다.
 */
const FREE_ASPECT_RANGE: AspectRange = { min: 0.75, max: 2 };
/** 위 범위를 화면에 보여줄 때 쓰는 표기. 범위를 바꾸면 이 문구도 함께 고친다. */
const FREE_ASPECT_GUIDE_TEXT = '2:1 ~ 3:4';

type DateOption = 'none' | 'optional';
type optionProps =
  | {
      readonly dateOption: DateOption;
      readonly thumbnailOption: 'none';
    }
  | {
      readonly dateOption: DateOption;
      readonly thumbnailOption: 'required' | 'optional';
      readonly thumbnail: {
        readonly optimalSize: { width: number; height: number };
        readonly minimumSize?: { width: number; height: number };
        /** 지정한 위치는 비율을 고정하지 않고 이 범위 안에서 자유롭게 자른다. */
        readonly aspectRange?: AspectRange;
        readonly preview: {
          readonly imageWidth: number;
          readonly isTitleCenter: boolean;
          readonly hasContentSummery: boolean;
          readonly hasBackgroundColor: boolean;
        };
      };
    };

const DisplaySections: React.FC<{
  options: BoardFormOptions['displaySectionOptions'];
}> = ({ options }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const { showError } = useSnackbar();

  const { control, setValue, getValues, reset } = useFormContext();
  const subject = getValues('subject');
  const content = getValues('content');
  const thumbnailFile = getValues('thumbnailFile');
  const useThumbnail = getValues('useThumbnail') === BooleanOption.TRUE;
  const displaySections = getValues('displaySections');
  const useThumbnailDisplayTime =
    getValues('useThumbnailDisplayTime') === BooleanOption.TRUE;
  const thumbnailDisplayStartedAt = getValues('thumbnailDisplayStartedAt');
  const thumbnailDisplayEndAt = getValues('thumbnailDisplayEndAt');

  const [isUploading, setIsUploading] = useState(false);
  const [showImageCropEditor, setShowImageCropEditor] = useState(false);

  const [selectedFile, setSelectedFile] = useState<File>();
  const [imageSrc, setImageSrc] = useState('');
  const [previewImageSrc, setPreviewImageSrc] = useState('');

  const optionProps: optionProps | undefined = useMemo(() => {
    switch (displaySections) {
      case 'bannerSection': {
        return {
          dateOption: 'optional',
          thumbnailOption: 'required',
          thumbnail: {
            optimalSize: { width: 2400, height: 1200 },
            preview: {
              imageWidth: 682,
              isTitleCenter: true,
              hasContentSummery: false,
              hasBackgroundColor: false,
            },
          },
        };
      }
      case 'rightFixSection': {
        return {
          dateOption: 'optional',
          thumbnailOption: 'required',
          thumbnail: {
            optimalSize: { width: 1200, height: 1200 },
            minimumSize: { width: 600, height: 600 },
            preview: {
              imageWidth: 324,
              isTitleCenter: false,
              hasContentSummery: false,
              hasBackgroundColor: true,
            },
          },
        };
      }
      case 'none': {
        return {
          dateOption: 'none',
          thumbnailOption: 'optional',
          thumbnail: {
            optimalSize: { width: 1920, height: 1080 },
            minimumSize: { width: 960, height: 540 },
            aspectRange: FREE_ASPECT_RANGE,
            preview: {
              imageWidth: 322,
              isTitleCenter: false,
              hasContentSummery: true,
              hasBackgroundColor: true,
            },
          },
        };
      }
      case 'rightSection': {
        return {
          dateOption: 'optional',
          thumbnailOption: 'optional',
          thumbnail: {
            optimalSize: { width: 1920, height: 1080 },
            minimumSize: { width: 960, height: 540 },
            aspectRange: FREE_ASPECT_RANGE,
            preview: {
              imageWidth: 322,
              isTitleCenter: false,
              hasContentSummery: true,
              hasBackgroundColor: true,
            },
          },
        };
      }
      case 'importantSection':
      case 'interviewSection': {
        return {
          dateOption: 'optional',
          thumbnailOption: 'none',
        };
      }
    }
  }, [displaySections]);

  const handleImageOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file || !optionProps || optionProps.thumbnailOption === 'none') {
      return;
    }

    try {
      validateFile([file], {
        isOnlyImage: true,
        maxLength: 1,
      });
    } catch (e) {
      if (e instanceof Error) {
        return showError({ message: e.message });
      } else {
        return showError({
          message: '이미지 선택에 에러가 발생했어요. 다시 시도해주세요',
        });
      }
    }

    const img = new Image();
    const imageSrc = URL.createObjectURL(file);

    img.src = imageSrc;
    img.onload = () => {
      event.target.value = '';
      setImageSrc(imageSrc);
      setShowImageCropEditor(true);
      setSelectedFile(file);
    };

    img.onerror = () => {
      event.target.value = '';
      showError({
        message: '이미지를 불러올 수 없습니다.',
      });
    };
  };

  const closeImageCropEditor = useCallback(() => {
    setImageSrc('');
    setShowImageCropEditor(false);
  }, []);

  const resetDateTime = useCallback(() => {
    reset((prevValue) => ({
      ...prevValue,
      useThumbnailDisplayTime: BooleanOption.FALSE,
      thumbnailDisplayStartedAt: null,
      thumbnailDisplayEndAt: null,
    }));
  }, [reset]);

  const handleOnError = useCallback(() => {
    showError({
      message: '이미지를 불러오는데 실패했습니다',
      onClose: () => {
        setImageSrc('');
      },
    });
  }, []);

  const handleOptionChange: (...event: any[]) => void = (event, newValue) => {
    const name = 'displaySections';
    const currentValue = getValues(name);
    const hasValue = Boolean(
      thumbnailFile ||
        (useThumbnailDisplayTime &&
          (thumbnailDisplayStartedAt || thumbnailDisplayEndAt)),
    );

    const setNewValue = () => {
      closeImageCropEditor();
      setIsUploading(false);
      setPreviewImageSrc('');
      setImageSrc('');
      setSelectedFile(undefined);

      const useThumbnail =
        newValue === 'none' ||
        newValue === 'interviewSection' ||
        newValue === 'importantSection' ||
        newValue === 'rightSection'
          ? BooleanOption.FALSE
          : BooleanOption.TRUE;

      reset((prevValue) => ({
        ...prevValue,
        displaySections: newValue,
        useThumbnail,
        showTitleInList: BooleanOption.TRUE,
        thumbnailFile: null,
        useThumbnailDisplayTime: BooleanOption.FALSE,
        thumbnailDisplayStartedAt: null,
        thumbnailDisplayEndAt: null,
      }));
    };

    if (hasValue && currentValue !== newValue) {
      dialog.show({
        message:
          '위치를 바꾸면 설정한 내용이 초기화 돼요.\n위치를 바꾸시겠어요?',
        confirmButtonText: '바꾸기',
        onConfirm: setNewValue,
        cancelButtonText: '취소',
        onCancel: () => setValue(name, currentValue),
      });
      return;
    }

    setNewValue();
  };

  const hasThumbnailOption = optionProps?.thumbnailOption === 'none';
  const minWidth = hasThumbnailOption
    ? 0
    : optionProps?.thumbnail?.minimumSize?.width;
  const minHeight = hasThumbnailOption
    ? 0
    : optionProps?.thumbnail?.minimumSize?.height;
  const optimalWidth = hasThumbnailOption
    ? 0
    : optionProps?.thumbnail?.optimalSize?.width ?? 0;
  const optimalHeight = hasThumbnailOption
    ? 1
    : optionProps?.thumbnail?.optimalSize?.height ?? 1;

  const useDateOption = (optionProps?.dateOption ?? 'optional') === 'optional';

  const aspectRange = hasThumbnailOption
    ? undefined
    : optionProps?.thumbnail?.aspectRange;
  const isFreeAspect = Boolean(aspectRange);
  // 목록 카드가 이미지를 카드 폭에 맞춰 늘리므로, 비율이 자유로울 때는 가로만 기준으로 안내한다.
  const minimumSizeGuideText = !(minWidth && minHeight)
    ? ''
    : isFreeAspect
    ? `최소: 가로 ${minWidth}px`
    : `최소: ${minWidth}x${minHeight}`;

  // 썸네일을 껐다 켜도 직전 선택은 남긴다. 잠깐 껐다는 이유로 고른 값을 버리면 다시 고르게 된다.
  const titleOptionValues = {
    displaySections,
    useThumbnail: getValues('useThumbnail'),
    showTitleInList: useWatch({ control, name: 'showTitleInList' }),
  };
  const canHideTitle = canHideTitleInList(titleOptionValues);
  const showTitleInList = isTitleShownInList(titleOptionValues);

  const imageCropEditorProps = useImageCropEditorProps({
    src: imageSrc,
    minWidth,
    minHeight,
    aspect: optionProps ? optimalWidth / optimalHeight : undefined,
    aspectRange,
    cropCanvasSize: 446,
    handleOnError,
  });

  useEffect(() => {
    if (thumbnailFile?.preview) {
      setPreviewImageSrc(thumbnailFile.preview);
    }
  }, [thumbnailFile?.preview]);

  const editThumbnailImage = async () => {
    try {
      if (!selectedFile) return;

      setIsUploading(true);
      const base64Image = imageCropEditorProps.getCroppedImage();
      const file = base64ToFile(base64Image, selectedFile.name);

      const uploadedFile = await uploadImage(file);
      setValue(
        'thumbnailFile',
        {
          key: uuid4(),
          name: uploadedFile.name,
          path: uploadedFile.url,
          preview: uploadedFile.url,
          status: 'new',
          type: getMimeTypeByExtension(uploadedFile.name),
          isUploaded: true,
        },
        { shouldValidate: true },
      );
      setSelectedFile(undefined);
    } catch (e) {
      dialog.show({
        message: '이미지 업로드에 실패했습니다',
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Controller
        name={'displaySections'}
        control={control}
        render={({ field }) => (
          <>
            <Label
              isRequired={false}
              title={'홈 화면에 보여줄 위치를 선택해 주세요.'}
              description={
                <>
                  ‘미노출하기’를 선택하면 <BoardLink /> 화면에서만 볼 수 있어요.
                </>
              }
              size={16}
            />
            <Radio
              {...field}
              row
              options={options}
              onChange={handleOptionChange}
            />
          </>
        )}
      />
      {optionProps && (
        <OptionWrapper>
          {optionProps.thumbnailOption !== 'none' && (
            <>
              <Modal open={showImageCropEditor} onClose={closeImageCropEditor}>
                <PopupContent onClick={(e) => e.stopPropagation()}>
                  <PopupHeader>
                    <PopupHeaderTitle>이미지 비율 조정</PopupHeaderTitle>
                    <IconButton
                      disableRipple
                      sx={{ marginLeft: 'auto', padding: 0 }}
                      onClick={closeImageCropEditor}
                    >
                      <CancelIcon />
                    </IconButton>
                  </PopupHeader>
                  <PopupBody>
                    <ImageCropEditor {...imageCropEditorProps} />
                    <GuideText>
                      선택된 사이즈:{' '}
                      {imageCropEditorProps.selectedImageSize.width}x
                      {imageCropEditorProps.selectedImageSize.height}
                    </GuideText>
                    {isFreeAspect && (
                      <GuideText>
                        {`가로세로 비율은 ${FREE_ASPECT_GUIDE_TEXT} 사이에서 자유롭게 조정할 수 있어요.`}
                      </GuideText>
                    )}
                    <ErrorMessage>
                      {imageCropEditorProps.isMinimumReached && (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <circle
                              cx="8"
                              cy="8"
                              r="5"
                              stroke="#F55060"
                              strokeWidth="0.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <rect
                              x="8"
                              y="10.2222"
                              width="0.00698242"
                              height="0.00698242"
                              stroke="#F55060"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8 8L8 5.77778"
                              stroke="#F55060"
                              strokeWidth="0.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <ErrorText>
                            {isFreeAspect
                              ? `이미지 가로가 ${minWidth}px 이하인 경우, 썸네일이 선명하지 않을 수 있습니다.`
                              : `이미지 사이즈가 ${minWidth}x${minHeight} 이하인 경우, 썸네일이 선명하지 않을 수 있습니다.`}
                          </ErrorText>
                        </>
                      )}
                    </ErrorMessage>
                  </PopupBody>
                  <PopupFooter>
                    <ButtonWrapper sx={{ width: '304px' }}>
                      <Button
                        variant={'outlined'}
                        size={'large'}
                        onClick={closeImageCropEditor}
                      >
                        {t('취소')}
                      </Button>
                      <Button
                        variant={'contained'}
                        size={'large'}
                        onClick={() => {
                          closeImageCropEditor();
                          editThumbnailImage();
                        }}
                      >
                        {t('확인')}
                      </Button>
                    </ButtonWrapper>
                  </PopupFooter>
                </PopupContent>
              </Modal>
              <ImageUploadWrapper
                className={classNames({
                  'has-next-item': useDateOption,
                })}
              >
                <div>
                  <Label
                    isRequired={false}
                    title="썸네일 이미지를 사용하실건가요?"
                    size={14}
                  />
                  <OptionBody>
                    <RHFRadio
                      name={'useThumbnail'}
                      options={[
                        { value: BooleanOption.TRUE, label: '사용 하기' },
                        { value: BooleanOption.FALSE, label: '사용 안하기' },
                      ]}
                    />
                  </OptionBody>
                </div>
                {useThumbnail && (
                  <>
                    <div>
                      <ImageUploadLabel>
                        {`이미지를 업로드해 주세요. (최적: ${optimalWidth}x${optimalHeight}${
                          minimumSizeGuideText
                            ? ` / ${minimumSizeGuideText}`
                            : ''
                        })`}
                      </ImageUploadLabel>
                      <ImageUploadFormWrapper>
                        <Button
                          component={'label'}
                          variant={'outlined'}
                          size={'large'}
                          loading={isUploading}
                          tabIndex={-1}
                        >
                          이미지 업로드
                          <VisuallyHiddenInput
                            type="file"
                            hidden
                            accept={'image/*'}
                            onChange={handleImageOnChange}
                            disabled={isUploading}
                          />
                        </Button>
                        <GrayText>{thumbnailFile?.name ?? ''}</GrayText>
                      </ImageUploadFormWrapper>
                    </div>
                    {canHideTitle && (
                      <TitleOptionWrapper>
                        <Label
                          isRequired={false}
                          title="목록에서 제목을 보여줄까요?"
                          description="이미지 안에 이미 제목이 있으면 ‘표시 안 함’을 선택해, 같은 제목이 두 번 보이지 않게 할 수 있어요."
                          size={14}
                        />
                        <OptionBody>
                          <RHFRadio
                            name={'showTitleInList'}
                            options={[
                              { value: BooleanOption.TRUE, label: '표시' },
                              {
                                value: BooleanOption.FALSE,
                                label: '표시 안 함',
                              },
                            ]}
                          />
                        </OptionBody>
                      </TitleOptionWrapper>
                    )}
                    {optionProps.thumbnail &&
                      (previewImageSrc ||
                        optionProps.thumbnailOption === 'optional') && (
                        <PreviewWrapper>
                          <PreviewHeader>
                            <GrayText>미리보기</GrayText>
                          </PreviewHeader>
                          <PreviewBody
                            sx={{
                              background: optionProps.thumbnail.preview
                                .hasBackgroundColor
                                ? '#fff'
                                : 'transparent',
                              width: `${optionProps.thumbnail.preview.imageWidth}px`,
                            }}
                          >
                            {previewImageSrc && (
                              <PreviewImg
                                component="img"
                                src={previewImageSrc}
                                alt={'선택한 이미지 미리보기'}
                              />
                            )}
                            {showTitleInList && (
                              <PreviewTitle
                                sx={{
                                  textAlign: optionProps.thumbnail.preview
                                    .isTitleCenter
                                    ? 'center'
                                    : 'initial',
                                  color: subject ? '#000' : '#888D96',
                                  fontWeight: subject ? 600 : 400,
                                }}
                              >
                                {subject || '제목'}
                              </PreviewTitle>
                            )}
                            {optionProps.thumbnail.preview
                              .hasContentSummery && (
                              <PreviewContentSummary>
                                {stripHTML(content)}
                              </PreviewContentSummary>
                            )}
                          </PreviewBody>
                        </PreviewWrapper>
                      )}
                  </>
                )}
              </ImageUploadWrapper>
            </>
          )}
          {useDateOption && (
            <DisplayDateWrapper>
              <DisplayDateHeader>
                <Label
                  isRequired={false}
                  title="홈 화면에 특정 기간에만 보이도록 할까요?"
                  description={
                    <>
                      설정하지 않으면 계속 보이고, 설정한 기간이 끝나면{'  '}
                      <BoardLink /> 화면에서만 볼 수 있어요.
                    </>
                  }
                  size={14}
                />
                <Button
                  disableRipple
                  disabled={isUploading}
                  endIcon={<RefreshIcon />}
                  onClick={resetDateTime}
                  sx={{
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20px',
                    color: 'var(--dk-text, #000)',
                    '& .MuiButton-endIcon': {
                      marginLeft: 0,
                    },
                    '&:hover, &:focus': {
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                    },
                  }}
                >
                  초기화
                </Button>
              </DisplayDateHeader>
              <DisplayDateBody>
                <RHFRadio
                  name={'useThumbnailDisplayTime'}
                  options={[
                    { value: BooleanOption.FALSE, label: '항상 보여주기' },
                    {
                      value: BooleanOption.TRUE,
                      label: '특정 기간에만 보여주기',
                    },
                  ]}
                />
              </DisplayDateBody>
              {useThumbnailDisplayTime && (
                <DisplayDateBody>
                  <RHFDateTimePicker
                    name={'thumbnailDisplayStartedAt'}
                    triggerField={
                      thumbnailDisplayEndAt
                        ? 'thumbnailDisplayEndAt'
                        : undefined
                    }
                  />
                  <Box sx={{ height: '44px', lineHeight: '44px' }}>-</Box>
                  <RHFDateTimePicker
                    name={'thumbnailDisplayEndAt'}
                    triggerField={
                      thumbnailDisplayStartedAt
                        ? 'thumbnailDisplayStartedAt'
                        : undefined
                    }
                  />
                </DisplayDateBody>
              )}
            </DisplayDateWrapper>
          )}
        </OptionWrapper>
      )}
    </>
  );
};

export default DisplaySections;

const RefreshIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 3.25C10.6234 3.25 12.75 5.37665 12.75 8C12.75 10.6234 10.6234 12.75 8 12.75C5.37665 12.75 3.25 10.6234 3.25 8C3.25 6.50783 3.93805 5.17637 5.01418 4.30556"
      stroke="black"
      strokeWidth="0.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.25 4.04199H5.36111V6.1531"
      stroke="black"
      strokeWidth="0.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CancelIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.5 6.5L6.5 17.5"
      stroke="black"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.5 6.5L17.5 17.5"
      stroke="black"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BoardLink = () => (
  <Link
    href={PAGE_URL.SOCIAL}
    target="_blank"
    sx={{
      textDecoration: 'none',
    }}
  >
    게시판
  </Link>
);
